<template>
  <validation-observer
    ref="createInvoiceForm"
    #default="{ invalid }"
  >
    <b-modal
      id="create-invoice-modal"
      size="lg"
      :title="$t('Buy and pay')"
      no-close-on-backdrop
      @ok="submit"
      @show="getData"
      @hidden="resetForm"
    >
      <b-form>
        <div v-if="invoiceType === 'buy'">
          <b-form-group :label="$t('Service')">
            <validation-provider
              #default="{ errors }"
              :name="$t('Service')"
              vid="package_id"
              rules="required"
            >
              <v-select
                v-model="form.package_id"
                :options="packages"
                :clearable="false"
                label="display_period"
                :reduce="_package => _package.id"
                :state="errors.length > 0 ? false : null"
                :disabled="loading"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>
        </div>
        <div v-if="invoiceType === 'message_packet' && products.length">
          <p class="text-center">
            {{ $t('Please select the number of messages') }}
          </p>
          <vue-slider
            v-model="form.quantity"
            :data="products"
            :data-value="'value'"
            :data-label="'value'"
            class="mx-2"
            direction="ltr"
            :disabled="loader || loading"
          />
        </div>
        <div class="mt-3">
          <div
            v-if="loader"
            class="d-flex justify-content-center mb-1"
          >
            <b-spinner label="Loading..." />
          </div>
          <div v-else-if="payment !== null">
            <!-- Invoice Description: Table -->
            <b-table-lite
              responsive
              :items="invoice.items"
              :fields="invoiceFields"
            >
              <template #cell(quantity)="data">
                {{ data.value }}
              </template>
              <template #cell(amount)="data">
                {{ data.value | numberFormat }} {{ $t('Currency') }}
              </template>
              <template #cell(discount)="data">
                {{ data.value | numberFormat }} {{ $t('Currency') }}
              </template>
              <template #cell(total_amount)="data">
                {{ data.value | numberFormat }} {{ $t('Currency') }}
              </template>
            </b-table-lite>
            <div
              class="d-flex justify-content-between mb-1"
            >
              <span>{{ $t('Amount') }}</span>
              <span class="font-weight-bold">{{ payment.amount | numberFormat }} {{ $t('Currency') }}</span>
            </div>
            <template v-for="(discount,key) in payment.discounts">
              <div
                v-if="discount.amount > 0"
                :key="key"
                class="d-flex justify-content-between mb-1 text-info"
              >
                <span>{{ discount.title }} <span v-if="discount.info.code > 0">({{ discount.info.code }})</span> <span v-if="discount.percent > 0">({{ discount.percent | numberFormat }}%)</span></span>
                <span class="font-weight-bold">{{ discount.amount | numberFormat }} {{ $t('Currency') }}</span>
              </div>
            </template>
            <div
              v-if="payment.discount > 0"
              class="d-flex justify-content-between mb-1 text-danger"
            >
              <span>{{ $t('Total discount') }}</span>
              <span class="font-weight-bold">{{ payment.discount | numberFormat }} {{ $t('Currency') }}</span>
            </div>
            <div class="d-flex justify-content-between mb-1 text-warning">
              <span>{{ $t('Tax amount') }} ({{ payment.tax_percent | numberFormat }}%)</span>
              <span class="font-weight-bold">{{ payment.tax_amount | numberFormat }} {{ $t('Currency') }}</span>
            </div>
            <div class="d-flex justify-content-between mb-1 text-success">
              <span>{{ $t('Payable amount') }}</span>
              <span class="font-weight-bold">{{ payment.total_amount | numberFormat }} {{ $t('Currency') }}</span>
            </div>
          </div>
        </div>
      </b-form>
      <template #modal-footer="{ ok }">
        <b-row class="w-100">
          <b-col
            cols="12"
            md="9"
          >
            <b-row v-if="payment">
              <b-col
                cols="7"
              >
                <b-form-input
                  id="discount_code"
                  v-model="form.discount_code"
                  type="text"
                  :placeholder="$t('Discount Code')"
                />
              </b-col>
              <b-col
                cols="5"
              >
                <!-- Button with custom close trigger value -->
                <b-button
                  variant="info"
                  :disabled="loading || form.discount_code.length === 0"
                  @click="applyDiscount"
                >
                  {{ $t('Apply Discount') }}
                </b-button>
              </b-col>
            </b-row>
          </b-col>
          <b-col
            cols="12"
            md="3"
          >
            <!-- Emulate built in modal footer ok and cancel button actions -->
            <b-button
              variant="success"
              block
              :disabled="invalid || loading"
              @click="ok()"
            >
              {{ $t('Buy and pay') }}
            </b-button>
          </b-col>
        </b-row>
      </template>
    </b-modal>
  </validation-observer>
</template>

<script>
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import {
  BRow,
  BCol,
  BModal, BForm, BFormInput, BFormGroup, BButton, BSpinner, BTableLite,
} from 'bootstrap-vue'
import VueSlider from 'vue-slider-component'
import vSelect from 'vue-select'
import { required } from '@validations'

export default {
  name: 'CreateInvoiceModal',
  components: {
    BRow,
    BCol,
    BModal,
    BForm,
    BFormInput,
    BFormGroup,
    BButton,
    BSpinner,
    BTableLite,
    vSelect,
    ValidationProvider,
    ValidationObserver,
    VueSlider,
  },
  props: {
    invoiceType: {
      type: String,
      default: () => '',
    },
    panelId: {
      type: [String, Number],
      default: () => {},
    },
    serviceId: {
      type: [String, Number],
      default: () => {},
    },
  },
  data() {
    return {
      loader: true,
      loading: false,
      form: {
        invoice_type: this.invoiceType,
        panel_id: this.panelId,
        service_id: this.serviceId,
        package_id: null,
        quantity: 2000,
        discount_code: '',
      },
      invoiceFields: [
        {
          key: 'item.name',
          label: this.$t('Item name'),
        },
        {
          key: 'amount',
          label: this.$t('Amount'),
        },
        // {
        //   key: 'quantity',
        //   label: this.$t('Quantity'),
        // },
        {
          key: 'discount',
          label: this.$t('Discount amount'),
        },
        {
          key: 'total_amount',
          label: this.$t('Total amount'),
        },
      ],
      packages: [],
      products: [],
      invoice: null,
      payment: null,
      required,
    }
  },
  watch: {
    'form.package_id': 'packageChanged',
    'form.quantity': 'quantityChanged',
  },
  methods: {
    getData() {
      this.loader = true
      if (this.invoiceType) this.form.invoice_type = this.invoiceType
      if (this.panelId) this.form.panel_id = this.panelId
      if (this.serviceId) this.form.service_id = this.serviceId
      this.$http.get('invoices/create', {
        params: this.form,
      }).then(({ data }) => {
        if (data.invoice) {
          this.invoice = data.invoice
          this.payment = this.invoice.payment
        }
        if (data.packages) {
          this.packages = data.packages
        }
        if (data.products) {
          this.products = data.products
        }
        if (data.package_id) {
          this.form.package_id = data.package_id
        }
        this.loader = false
      }).catch(() => {
        this.loader = false
        if (this.form.discount_code) {
          this.form.discount_code = ''
          this.getData()
        }
      })
    },
    applyDiscount() {
      if (this.form.discount_code.length > 0) {
        this.getData()
      }
    },
    packageChanged() {
      if (this.form.package_id !== null) {
        this.form.discount_code = ''
        this.getData()
      }
    },
    quantityChanged() {
      if (this.form.quantity !== null) {
        this.form.discount_code = ''
        this.getData()
      }
    },
    submit(bvModalEvent) {
      bvModalEvent.preventDefault()
      this.$refs.createInvoiceForm.validate().then(success => {
        if (success) {
          this.loading = true
          this.$http
            .post('invoices', this.form)
            .then(({ data }) => {
              this.loading = true
              this.startPayment(data.payment.id)
            })
            .catch(() => {
              this.loading = false
            })
        }
      })
    },
    startPayment(paymentId) {
      this.loading = true
      this.$http
        .post(`payments/${paymentId}/start`)
        .then(({ data }) => {
          let coupon = ''
          let selectedPackage = ''
          const { payment } = data
          if (payment.discounts) {
            payment.discounts.forEach(discount => {
              if (discount.name === 'code') {
                coupon = discount.info.code
              }
            })
          }
          // gtm event
          if (this.invoiceType === 'buy') {
            this.packages.forEach(value => {
              if (value.id == this.form.package_id) {
                selectedPackage = value
              }
            })
            this.$gtm.trackEvent({
              event: 'buy_package',
              'plan-type': selectedPackage.panel.name, // plan name --> basic, standard, advanced, unlimited
              'plan-time': selectedPackage.period, // 1month, 3months, 6months,12months
              price: selectedPackage.panel.amount, // Gheymat_plan
              coupon,
              value: payment.total_amount, // Mablagh_Pardakht_shode
            })
          } else if (this.invoiceType === 'message_packet') {
            this.$gtm.trackEvent({
              event: 'buy_message',
              'message-count': this.form.quantity, // 2000,5000,10000,20000 ,....
              price: payment.amount, // Gheymat_plan
              coupon,
              value: payment.total_amount, // Mablagh_Pardakht_shode
            })
          }
          if (data.action !== undefined) {
            const form = document.createElement('form')
            form.action = data.action
            form.method = data.method
            const { inputs } = data
            Object.keys(inputs).forEach(name => {
              const input = document.createElement('input')
              input.value = inputs[name]
              input.name = name
              form.appendChild(input)
            })
            document.body.appendChild(form)
            form.submit()
          } else {
            return this.$router.push({ name: 'accounts-show', params: { id: payment.account.id } })
          }
        })
        .catch(() => {
          this.loading = false
        })
    },
    resetForm() {
      this.form.panel_id = null
      this.form.service_id = null
      this.form.package_id = null
      this.form.quantity = 2000
      this.form.discount_code = ''
      this.payment = null
    },
  },
}
</script>

<style lang="scss" >
@import '@core/scss/vue/libs/vue-slider.scss';
</style>
